import { Col, Container, Row } from 'react-bootstrap'
import React, { useState } from 'react'
import { useGetAccountQuery, useUpdateAccountMutation } from '../../../../store/accountUserSettings/accountUserSettings'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { SelectDropdown, SelectOption } from '../../../../components/SelectDropdown/SelectDropdown'
import { SuccessToast } from '../../../../components/SuccessToast/SuccessToast'
import { ACCOUNT_REGION_OPTIONS, AccountRegion } from '../../../../models/Account'

const DEFAULT_REGION: AccountRegion = 'en-US'

export const AccountRegionalSettings: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)

  const [showChangesSaved, setShowChangesSaved] = useState<boolean>(false)

  const { data: account, isFetching: accountLoading } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const [updateAccount, { isLoading: accountSaveLoading }] = useUpdateAccountMutation()

  const [pendingRegion, setPendingRegion] = useState<AccountRegion | null>(null)
  const selectedRegion = pendingRegion ?? account?.data.effectiveConfig.region ?? DEFAULT_REGION

  const selectedOption = ACCOUNT_REGION_OPTIONS.find((option) => option.id === selectedRegion) ?? null

  const handleRegionSelected = async (option: SelectOption) => {
    const region = option.id as AccountRegion
    if (!account || region === selectedRegion) {
      return
    }
    const previousRegion = pendingRegion
    setPendingRegion(region)
    try {
      await updateAccount({
        accountId,
        payload: {
          config: {
            ...account.data.config,
            region,
          },
        },
      }).unwrap()

      setShowChangesSaved(true)
    } catch {
      setPendingRegion(previousRegion)
    }
  }

  return (
    <Container>
      <Row className='zocks-mb-2'>
        <Col>
          <h4 className='zocks-text-gray-900 mb-2'>Regional settings</h4>
        </Col>
      </Row>
      <Row className='mb-4'>
        <Col>
          <p className='zocks-text-gray-900 p-0 m-0'>Match generated outputs to the conventions of the selected region.</p>
          <p className='zocks-text-gray-900 p-0 m-0 mt-2'>
            This controls spelling, terminology, and regional writing preferences in summaries, AI Assistant answers, and emails.
          </p>
          <p className='zocks-text-gray-900 p-0 m-0 mt-2'>
            This does not change meeting transcription language, the Zocks interface language, or the currency mentioned in generated outputs.{' '}
          </p>
        </Col>
      </Row>
      <Row className='mb-1'>
        <div className='paragraph-medium'>Output region</div>
      </Row>
      <Row className='mb-4'>
        <Col className='pe-2'>
          <ScopedBlock
            scope={{
              scope: 'account:update',
              superScope: ['account:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <SelectDropdown
              options={ACCOUNT_REGION_OPTIONS}
              initialSelectedOption={selectedOption}
              onOptionSelected={handleRegionSelected}
              loading={accountLoading && !account}
              disabled={accountSaveLoading || accountLoading}
              className='py-2 zocks-px-0.75 zocks-border-color-gray-500'
            />
          </ScopedBlock>
        </Col>
      </Row>

      <SuccessToast message='Changes saved' show={showChangesSaved} onClose={() => setShowChangesSaved(false)} delay={2000} />
    </Container>
  )
}
